import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, clearAuthCookies } from '../lib/supabase';
import { auth, db } from '../lib/supabase';

const AuthContext = createContext({});

const SAFE_ERRORS = {
  INVALID_CREDENTIALS: 'Invalid email or password.',
  SIGNUP_FAILED: 'Could not create account. Please try again.',
  VERIFICATION_FAILED: 'Security verification failed. Please try again.',
  NETWORK_ERROR: 'Connection error. Please check your internet.',
  UPDATE_FAILED: 'Could not update. Please try again.',
  GENERIC: 'Something went wrong. Please try again.',
};

const sanitizeError = (err) => {
  const msg = (err?.message || err || '').toLowerCase();
  if (msg.includes('invalid login') || msg.includes('invalid email') || msg.includes('invalid password')) {
    return SAFE_ERRORS.INVALID_CREDENTIALS;
  }
  if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
    return 'Email not confirmed. Please check your inbox and confirm your email address.';
  }
  if (msg.includes('email') && msg.includes('already')) {
    return 'An account with this email already exists.';
  }
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch')) {
    return SAFE_ERRORS.NETWORK_ERROR;
  }
  if (msg.includes('password')) {
    return 'Password does not meet the requirements.';
  }
  return SAFE_ERRORS.GENERIC;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AUTH_RATE_LIMIT = {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000,
  attempts: [],
};

const checkRateLimit = () => {
  const now = Date.now();
  AUTH_RATE_LIMIT.attempts = AUTH_RATE_LIMIT.attempts.filter(
    (t) => now - t < AUTH_RATE_LIMIT.windowMs
  );
  if (AUTH_RATE_LIMIT.attempts.length >= AUTH_RATE_LIMIT.maxAttempts) {
    const waitMinutes = Math.ceil(
      (AUTH_RATE_LIMIT.windowMs - (now - AUTH_RATE_LIMIT.attempts[0])) / 60000
    );
    return { allowed: false, waitMinutes };
  }
  AUTH_RATE_LIMIT.attempts.push(now);
  return { allowed: true };
};

const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setProfile(data);
      return data;
    } catch (err) {
      setError(SAFE_ERRORS.GENERIC);
      return null;
    }
  }, []);

  const performSignOut = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (e) {}
    setUser(null);
    setProfile(null);
    clearAuthCookies();
  }, []);

  useEffect(() => {
    if (!user) return;

    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        performSignOut();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
          performSignOut();
        }, 60 * 60 * 1000);
      } else {
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, performSignOut]);

  const processReferral = useCallback(async (currentUser) => {
    const pendingCode = currentUser.user_metadata?.pending_referral_code;
    if (!pendingCode) return;

    try {
      const { data: rpcResult, error: rpcError } = await supabase.rpc('process_referral', { referral_code_input: pendingCode });
      if (rpcError) {
        console.warn('[REFERRAL DEBUG] RPC error:', rpcError.message, rpcError.code);
      } else {
        console.warn('[REFERRAL DEBUG] RPC result:', JSON.stringify(rpcResult));
      }
      await supabase.auth.updateUser({ data: { pending_referral_code: null } });
    } catch (e) {
      console.warn('[REFERRAL DEBUG] catch:', e.message);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let initialCheckDone = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (!initialCheckDone) {
        initialCheckDone = true;
        clearTimeout(fallbackTimer);
      }

      if (session?.user) {
        setUser(session.user);
        setLoading(false);
        fetchProfile(session.user.id);
        if (event === 'SIGNED_IN') {
          processReferral(session.user);
        }
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    const fallbackTimer = setTimeout(() => {
      if (!initialCheckDone && mounted) {
        initialCheckDone = true;
        setLoading(false);
      }
    }, 2000);

    return () => {
      mounted = false;
      clearTimeout(fallbackTimer);
      subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  const verifyTurnstile = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-turnstile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        return true;
      }
      return false;
    }
  };

  const signUp = async (email, password, turnstileToken = null, referralCode = null) => {
    setLoading(true);
    setError(null);

    try {
      const rateCheck = checkRateLimit();
      if (!rateCheck.allowed) {
        throw new Error(`Too many attempts. Please wait ${rateCheck.waitMinutes} minutes.`);
      }

      if (turnstileToken) {
        const isValid = await verifyTurnstile(turnstileToken);
        if (!isValid) {
          throw new Error(SAFE_ERRORS.VERIFICATION_FAILED);
        }
      }

      if (user) {
        await auth.signOut();
        setUser(null);
        setProfile(null);
        clearAuthCookies();
      }

      const metadata = referralCode ? { pending_referral_code: referralCode } : {};
      const { data, error } = await auth.signUp(email, password, metadata);
      if (error) throw error;

      // Fire-and-forget auth log
      if (data?.session?.access_token) {
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/auth-log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ event: 'signup' }),
        }).catch(() => {});
      }

      return { success: true, data };
    } catch (err) {
      const safeMsg = err.message?.startsWith('Too many') ? err.message : sanitizeError(err);
      setError(safeMsg);
      return { success: false, error: safeMsg };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password, turnstileToken = null) => {
    setLoading(true);
    setError(null);

    try {
      const rateCheck = checkRateLimit();
      if (!rateCheck.allowed) {
        throw new Error(`Too many attempts. Please wait ${rateCheck.waitMinutes} minutes.`);
      }

      if (turnstileToken) {
        const isValid = await verifyTurnstile(turnstileToken);
        if (!isValid) {
          throw new Error(SAFE_ERRORS.VERIFICATION_FAILED);
        }
      }

      const { data, error } = await auth.signIn(email, password);
      if (error) throw error;

      setUser(data.user);
      await fetchProfile(data.user.id);

      // Fire-and-forget auth log
      if (data?.session?.access_token) {
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/auth-log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ event: 'login' }),
        }).catch(() => {});
      }

      return { success: true, data };
    } catch (err) {
      const safeMsg = err.message?.startsWith('Too many') ? err.message : sanitizeError(err);
      setError(safeMsg);
      return { success: false, error: safeMsg };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);

    try {
      await performSignOut();
      return { success: true };
    } catch (err) {
      setError(SAFE_ERRORS.GENERIC);
      return { success: false, error: SAFE_ERRORS.GENERIC };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    setLoading(true);
    setError(null);

    try {
      const rateCheck = checkRateLimit();
      if (!rateCheck.allowed) {
        throw new Error(`Too many attempts. Please wait ${rateCheck.waitMinutes} minutes.`);
      }

      const { error } = await auth.resetPassword(email);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      const safeMsg = err.message?.startsWith('Too many') ? err.message : SAFE_ERRORS.GENERIC;
      setError(safeMsg);
      return { success: false, error: safeMsg };
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await auth.updatePassword(newPassword);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      setError(SAFE_ERRORS.UPDATE_FAILED);
      return { success: false, error: SAFE_ERRORS.UPDATE_FAILED };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await db.profiles.update(user.id, updates);
      if (error) throw error;

      if (updates.proxy_password && (data.stock_subuser_id_basic || data.stock_subuser_id_premium || data.stock_subuser_id_monthly)) {
        supabase.functions.invoke('stock-api', {
          body: {
            action: 'update-subuser-password',
            proxy_password: updates.proxy_password,
          },
        }).then(() => {
          db.profiles.update(data.id, {
            stock_proxy_password: updates.proxy_password,
          });
        }).catch(() => {});
      }

      setProfile(data);
      return { success: true, data };
    } catch (err) {
      setError(SAFE_ERRORS.UPDATE_FAILED);
      return { success: false, error: SAFE_ERRORS.UPDATE_FAILED };
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (!user) return null;
    return await fetchProfile(user.id);
  };

  const clearError = () => setError(null);

  const value = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    refreshProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
